/**
 * "Precisa da sua atenção" (GUPM-206, fiel ao buildCriticalNotifBlock do V8, L1330).
 * Cards no padrão `crit-card` do protótipo (borda-esquerda 4px + título em tom claro
 * por severidade), com texto DIFERENCIADO POR PERFIL:
 *  - Vigilante (operador): linguagem simples ("o que fazer agora").
 *  - RT/Admin: linguagem técnica (valor, unidade, seção).
 * Ordenados por severidade (emergência no topo). Sem anomalia a seção não
 * renderiza (GUPM-221) — diverge do empty state do V8 de propósito.
 * O botão leva ao auto-open do fluxo TARP (comportamento herdado do GUPM-185) e
 * fica NA LINHA do texto, não abaixo dele (GUPM-221): o card tem sobra horizontal
 * de sobra e a terceira linha só gastava altura — com vários instrumentos em nível
 * ao mesmo tempo, é ela que empurra o resto do painel para fora da tela.
 */
import {
  LEVEL_UI, LevelIcon, NIVEL_EXIBIDO_UI, TITLE_COLOR, TITLE_COLOR_SEM_SINAL,
  type TarpLevel,
} from '@/ui';
import { SEM_SINAL } from '@/domain/instrumentos/nivel-exibido';
import {
  descricaoDoItemDeAtencao, rotuloDaAcaoDeAtencao, tituloDoItemDeAtencao,
} from '@/domain/dashboard/textos-atencao';
import { rotuloDaEstrutura } from '@/domain/estrutura/rotulo';
import { useAgora } from '@/hooks/useAgora';
import type { UserProfile } from '@/hooks/useAuth';
import type { ItemAtencao } from './useM1Data';

interface Props {
  badInstruments: ItemAtencao[];
  profile: UserProfile | null;
  /**
   * Abre o fluxo TARP do card de nível. C-88: recebe o **id** — o código se repete entre
   * as barragens (INA-02 existe na A e na B) e escolheria o primeiro que casasse.
   */
  onTarp: (instrumentId: string) => void;
  /**
   * GUPM-303 — abre a TARP de perda de sinal. Recebe o **id**, não o código: `TIL-01`
   * existe nas três barragens (produção, 24/08/2026), e o `onTarp` por código escolheria
   * o primeiro que casasse — despachando equipe para a estrutura errada.
   */
  onTarpSemSinal: (instrumentId: string) => void;
}

const SEV_RANK: Record<TarpLevel, number> = { EMERGENCIA: 3, ALERTA: 2, ATENCAO: 1, NORMAL: 0 };

/**
 * Severidade do card para ordenação. Perda de sinal entra como **ALERTA**, que é o nível
 * do protocolo `TARP-PS-TILT` no catálogo — não é escolha de cor, é o enquadramento que o
 * catálogo já dá ao evento.
 */
function sevDoItem(item: ItemAtencao): number {
  return item.kind === 'sem-sinal' ? SEV_RANK.ALERTA : SEV_RANK[item.level];
}

export default function AnomalyList({ badInstruments, profile, onTarp, onTarpSemSinal }: Props) {
  const isVigilante = profile === 'operador';
  // Só liga o relógio quando existe card que depende dele.
  const agora = useAgora(badInstruments.some((b) => b.kind === 'sem-sinal'));

  // Sem anomalia, a seção inteira sai da tela (GUPM-221). O V8 mostrava aqui um
  // empty state "Sem alertas críticos"; ele repete o que o semáforo consolidado
  // logo acima já afirma — e é o semáforo que garante que "seção ausente" não
  // seja lido como "falhou ao carregar".
  if (badInstruments.length === 0) return null;

  return (
    <div className="mb-3">
      <h2 className="mb-2 flex items-center gap-2 text-[15px] font-bold text-white">
        🔔 Precisa da sua atenção
      </h2>

      {/* crit-stack do V8: pilha de crit-cards com gap 10px. */}
      <div className="flex flex-col gap-2.5">
        {[...badInstruments]
          .sort((a, b) => sevDoItem(b) - sevDoItem(a))
          .map((item) => {
            // Perda de sinal vem da paleta de EXIBIÇÃO, não da de nível — ainda que
            // hoje as duas coincidam no matiz (o catálogo dá ALERTA ao `TARP-PS-TILT`).
            // Manter a origem separada importa: o dia em que o protocolo mudar de
            // nível, o card acompanha sem ninguém precisar lembrar deste ponto.
            const ui = item.kind === 'sem-sinal' ? NIVEL_EXIBIDO_UI[SEM_SINAL] : LEVEL_UI[item.level];
            const tituloColor =
              item.kind === 'sem-sinal' ? TITLE_COLOR_SEM_SINAL : TITLE_COLOR[item.level];
            const code = item.instrument.code;
            return (
              <div
                key={item.instrument.id}
                className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border-l-4 px-[15px] py-[13px]"
                style={{ background: ui.bg, borderLeftColor: ui.color }}
              >
                <span className="shrink-0" style={{ color: ui.color }} aria-hidden>
                  {/* `LevelIcon` só conhece os 4 níveis medidos — perda de sinal não é
                      nível, então usa o glifo do token de exibição. */}
                  {item.kind === 'sem-sinal' ? (
                    <span className="text-[18px] leading-none">{ui.icon}</span>
                  ) : (
                    <LevelIcon level={item.level} size={20} />
                  )}
                </span>
                {/* `flex-1` empurra o botão para a direita; o `min-w` é o gatilho da
                    quebra — abaixo dele o `flex-wrap` do card manda o botão para a
                    linha de baixo em vez de espremer a leitura (regra #10).
                    140px é calibrado: no iPhone de 375px a caixa de conteúdo do card
                    tem 309px, e 20(ícone)+12+140+12+118(botão) = 302 ainda cabe numa
                    linha; num 320px não cabe e o botão desce. Deixar sem piso não
                    resolve — `min-width:auto` só respeita o min-content e a 260px
                    largava a coluna em 66px, inflando o card para 133px. */}
                <div className="min-w-[140px] flex-1">
                  <div
                    className="text-[13px] font-extrabold leading-[1.35]"
                    style={{ color: tituloColor }}
                  >
                    {tituloDoItemDeAtencao(item, isVigilante, agora)}
                  </div>
                  <div className="mt-[3px] text-[11.5px] leading-[1.5] text-jf-muted">
                    {descricaoDoItemDeAtencao(item, isVigilante)}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    item.kind === 'sem-sinal'
                      ? onTarpSemSinal(item.instrument.id)
                      : onTarp(item.instrument.id)
                  }
                  aria-label={
                    item.kind === 'sem-sinal'
                      ? `${rotuloDaAcaoDeAtencao(item)} para ${code} da ${rotuloDaEstrutura(item.damCode) ?? 'estrutura não identificada'}`
                      : `${rotuloDaAcaoDeAtencao(item)} para ${code}`
                  }
                  className="shrink-0 whitespace-nowrap rounded-lg border px-3 py-1.5 text-[11px] font-bold"
                  style={{ borderColor: ui.color, color: ui.color }}
                >
                  {rotuloDaAcaoDeAtencao(item)}
                </button>
              </div>
            );
          })}
      </div>
    </div>
  );
}
